/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import LibraryRichText from 'rich-text-to-react';
import { Typography, Paragraph, List } from '@telus-uds/components-web';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';

const defaultOptions = (isItalicNote, resolvedColor) => ({
  [BLOCKS.HEADING_1]: (node, key, next) => (
    <Typography key={key} heading="h1" variant={{ size: 'h1' }} block>
      {next(node.content, key, next)}
    </Typography>
  ),
  [BLOCKS.HEADING_2]: (node, key, next) => (
    <Typography key={key} heading="h2" variant={{ size: 'h2' }} block>
      {next(node.content, key, next)}
    </Typography>
  ),
  [BLOCKS.HEADING_3]: (node, key, next) => (
    <Typography key={key} heading="h3" variant={{ size: 'h3' }} block>
      {next(node.content, key, next)}
    </Typography>
  ),
  [BLOCKS.HEADING_4]: (node, key, next) => (
    <Typography key={key} heading="h4" variant={{ size: 'h4' }} block>
      {next(node.content, key, next)}
    </Typography>
  ),
  [BLOCKS.HEADING_5]: (node, key, next) => (
    <Typography key={key} heading="h5" variant={{ size: 'h5' }} block>
      {next(node.content, key, next)}
    </Typography>
  ),
  [BLOCKS.PARAGRAPH]: (node, key, next) => {
    const fontStyle = {
      fontStyle: isItalicNote === true ? 'italic' : 'normal',
      fontSize: isItalicNote === true ? '13px' : '100%',
      color: resolvedColor
    };
    return (
      <Paragraph
        key={key}
        style={{ lineHeight: '1.5rem', marginBottom: '1em', ...fontStyle }}
      >
        <Typography>{next(node.content, key, next)}</Typography>
      </Paragraph>
    );
  },
  [BLOCKS.UL_LIST]: (node, key, next) => (
    <List key={key}>{next(node.content, key, next)}</List>
  ),
  [BLOCKS.LIST_ITEM]: (node, key, next) => (
    <List.Item key={key}>
      {next(node.content, key, next)}
    </List.Item>
  ),
  [INLINES.HYPERLINK]: (node, key, next) => (
    <a href={node.data.uri} rel="noopener noreferrer" target="_blank">
      <Typography>{next(node.content, key, next)}</Typography>
    </a>
  ),
  [BLOCKS.EMBEDDED_ASSET]: node => {
    const {
      data: {
        target: {
          fields: {
            title,
            file: { url }
          }
        }
      }
    } = node;
    return (
      <img alt={title} key={url} src={url} style={{ marginTop: '2rem' }} />
    );
  }
});

const RichText = ({
  data = null,
  isItalicNote = false,
  isColorRed = false,
  textColor = '',
  options = {}
}) => {
  const { renderMark = {}, renderNode = {} } = options;
  const resolvedColor = textColor || (isColorRed === true ? 'red' : '');

  const mergedOptions = {
    renderMark: {
      ...renderMark,
      [MARKS.BOLD]: (text, key) => {
        const fontStyle = {
          fontStyle: isItalicNote === true ? 'italic' : 'normal',
          fontSize: isItalicNote === true ? '13px' : '100%',
          color: resolvedColor
        };
        return (
          <strong key={key} style={{ fontWeight: '900', ...fontStyle }}>
            {text}
          </strong>
        );
      },
      [MARKS.ITALIC]: (text, key) => (
        <span
          key={key}
          style={{ fontStyle: isItalicNote === true ? 'italic' : 'normal' }}
        >
          {text}
        </span>
      ),
      [MARKS.CODE]: (text, key) => (
        <strong key={key} style={{ fontWeight: '900', color: 'red' }}>
          {text}
        </strong>
      ),
      [MARKS.UNDERLINE]: (text, key) => (
        <strong
          key={key}
          style={{
            fontWeight: 'bold',
            textDecoration: 'underline',
            color: 'blue'
          }}
        >
          {text}
        </strong>
      )
    },
    renderNode: {
      ...defaultOptions(isItalicNote, resolvedColor),
      ...renderNode
    }
  };
  return data && <LibraryRichText document={data} options={mergedOptions} />;
};

RichText.propTypes = {
  data: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  isItalicNote: PropTypes.bool,
  isColorRed: PropTypes.bool,
  textColor: PropTypes.string,
  options: PropTypes.shape({
    renderMark: PropTypes.shape({}),
    renderNode: PropTypes.shape({})
  })
};

export default RichText;
